import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import {
    EntryRightDelegeeDetails,
    GetDelegatedEntryRightsQuery,
    getDelegatedEntryRights,
    getDelegeesFlatMyFleet,
    getDelegeeDetails,
    inviteDelegee,
    InviteEntryRightResponse,
    resendAllInvites,
    ResendAllInvitesModel,
    resendInvite,
    ResendInviteModel,
    updateDelegee,
    UpdateEntryDelegeePayload,
    UpdateEntryRightResponse,
    startBulkInvitations,
    validateDelegees,
    getBulkImportProgress,
    getBulkImportStatus,
} from "../../api/entryRights.api";
import { StartBulkInvitationsRequestModel } from "../../models/bulkImport/StartBulkInvitationsRequestModel";
import { ValidateDelegeesRequestModel } from "../../models/bulkImport/ValidateDelegeesRequestModel";
import { ValidateDelegeesResponseModel } from "../../models/bulkImport/ValidateDelegeesResponseModel";
import { GetBulkImportStatusResponseModel } from "../../models/bulkImport/GetBulkImportStatusResponseModel";
import { ErrorDetailsModel, tryAxios, ValidationResult } from "../../models/ErrorDetailsModel";
import { ForFleetManager } from "../../models/ForFleetManager";
import { InviteDelegeeRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { DelegatedEntryRight, EntryRightDelegeesWithPagination, EntryRightsMyFleetOverviewWithPagination } from "../../models/delegations/DelegatedEntryRight";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";
import { entryRightTypes } from "../action-types/entryRight.types";
import { failure, request, success } from "../helpers/action.helper";
import { toastActions } from "./toast.actions";
import { RevokeEntryRightsModel } from "../../models/vehicle-assignments/RevokeEntryRightsModel";
import { DeleteDelegeesPayload } from "../../models/vehicle-assignments/DeleteDelegeesPayload";
import { RootReducer } from "../reducers";
import { ProductContractTypeFilter } from "../../api/common";
import { ParkingProductType } from "../../models";
import { RevokeEntryRightsResponse } from "../../models/delegations/RevokeEntryRightsResponse";
import { setTodayActivationDatesToNowIfToday } from "../../utils/activationDate.utils";
import { checkForSeasonTicketCrmIdNotValidError } from "../../utils";

export interface EntryRightResponse<Q> {
    query: Q;
};

export interface EntryRightSuccessResponse<T, Q> extends EntryRightResponse<Q> {
    totalPages: number;
    totalRecords: number;
    data: T[];
}
type ThunkApiConfig = {
    rejectValue: ErrorDetailsModel;
    state: RootReducer;
};

export const getDelegatedEntryRightsThunk = createAsyncThunk<
    EntryRightSuccessResponse<DelegatedEntryRight, GetDelegatedEntryRightsQuery>,
    GetDelegatedEntryRightsQuery,
    ThunkApiConfig
>(entryRightTypes.GET_MY_PRODUCTS_ENTRY_RIGHTS, async (query, { rejectWithValue, signal }) => {
    try {
        const response = await getDelegatedEntryRights(query, { signal });

        return {
            query,
            totalPages: response.data.totalPages,
            totalRecords: response.data.totalRecords,
            data: response.data.delegatedEntryRights,
        };
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data as ErrorDetailsModel);
        }

        throw error;
    }
});


export type GetTotalRecordsQuery = {
    seasonTicketOwnerCrmId: string;
    language: string;
    placeId: number;
    productType: ParkingProductType
}

export type GetTotalRecordsResult = {
    totalRecords: number;
}

export const getTotalRecords = createAsyncThunk<
    GetTotalRecordsResult,
    GetTotalRecordsQuery,
    ThunkApiConfig
>(entryRightTypes.GET_TOTAL_RECORDS, async (query, { rejectWithValue, signal }) => {
    try {
        const response = await getDelegatedEntryRights({
            seasonTicketOwnerCrmId: query.seasonTicketOwnerCrmId,
            placeId: query.placeId,
            language: query.language,
            page: 1,
            pageSize: 1,
            productType: query.productType
        }, { signal });

        return {
            totalRecords: response.data.totalRecords,
        };
    } catch (error) {
        if (error instanceof AxiosError) {
            return rejectWithValue(error.response?.data as ErrorDetailsModel);
        }

        throw error;
    }
});

export const revokeEntryRightsRequestAction = createAction(request(entryRightTypes.REVOKE_ENTRY_RIGHTS));
export type RevokeEntryRightsSuccessPayload = {
    model: RevokeEntryRightsModel;
    response: RevokeEntryRightsResponse;
};
export const revokeEntryRightsSuccessAction = createAction<RevokeEntryRightsSuccessPayload>(success(entryRightTypes.REVOKE_ENTRY_RIGHTS));
export const revokeEntryRightsFailureAction = createAction(failure(entryRightTypes.REVOKE_ENTRY_RIGHTS));

export const deleteEntryDelegeesRequestAction = createAction(request(entryRightTypes.DELETE_ENTRY_RIGHT_DELEGEES));
export const deleteEntryDelegeesSuccessAction = createAction<DeleteDelegeesPayload>(success(entryRightTypes.DELETE_ENTRY_RIGHT_DELEGEES));
export const deleteEntryDelegeesFailureAction = createAction(failure(entryRightTypes.DELETE_ENTRY_RIGHT_DELEGEES));

export const toggleShowRevokeEntryRightsAction = createAction(entryRightTypes.TOGGLE_SHOW_REVOKE_ENTRY_RIGHT);
export const toggleShowDeleteEntryRightsDelegeesAction = createAction(entryRightTypes.TOGGLE_SHOW_DELETE_ENTRY_RIGHT_DELEGEES);

export const inviteActionThunk = createAsyncThunk<
    ValidationResult<InviteEntryRightResponse>,
    InviteDelegeeRequestModel,
    ThunkApiConfig
>(entryRightTypes.INVITE_DELEGEE, async (req, { dispatch }) => {
    const showErrorToast = () => dispatch(toastActions.error({
        content: {
            key: "parkingRights.inviteDelegee.error",
        },
    }));
    const requestModel = {
        ...req,
        products: setTodayActivationDatesToNowIfToday(req.products),
    };
    const res = await tryAxios(inviteDelegee(requestModel), () => {
        showErrorToast();
    });
    if (!res.ok) {
        if (checkForSeasonTicketCrmIdNotValidError(res.error)) {
            dispatch(toastActions.error({
                content: {
                    key: "errors.invalidSeasonTicketCrmId",
                },
            }));
        } else {
            showErrorToast();
        }
    }
    else {
        dispatch(toastActions.success({
            content: {
                key: "parkingRights.resendInvite.success",
            }
        }));
    }

    return res;
});

export const startBulkInvitationsThunk = createAsyncThunk<
    void,
    {
        seasonTicketOwnerCrmId: string;
        fileReference: string;
        request: StartBulkInvitationsRequestModel;
        onSuccess?: () => void;
    },
    ThunkApiConfig
>(
    entryRightTypes.START_BULK_INVITATIONS,
    async ({ seasonTicketOwnerCrmId, fileReference, request, onSuccess }, { rejectWithValue, dispatch }) => {
        try {
            await startBulkInvitations(seasonTicketOwnerCrmId, fileReference, request);

            // Call the success callback if provided
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            dispatch(toastActions.error({
                content: {
                    key: "entryRights.startBulkInvitations.error",
                },
            }));

            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            }
            throw error;
        }
    }
);

export const validateDelegeesThunk = createAsyncThunk<
    { fileReference: string },
    {
        seasonTicketOwnerCrmId: string;
        request: ValidateDelegeesRequestModel;
        onSuccess?: (response: ValidateDelegeesResponseModel) => void;
        onError?: (errorCode: string) => void;
    },
    ThunkApiConfig
>(
    entryRightTypes.VALIDATE_DELEGEES,
    async ({ seasonTicketOwnerCrmId, request, onSuccess, onError }, { rejectWithValue }) => {
        try {
            const response = await validateDelegees(seasonTicketOwnerCrmId, request);

            // Call the success callback if provided
            if (onSuccess) {
                onSuccess(response.data);
            }

            // Return the payload that matches what the original action was storing
            return { fileReference: response.data.fileReference };
        } catch (error) {
            if (error instanceof AxiosError) {
                const errorCode = getFirstErrorCode(error);

                if (onError) {
                    onError(errorCode);
                }

                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            }
            throw error;
        }
    }
);

export const getBulkImportProgressThunk = createAsyncThunk<
    {
        progress: number;
        hasErrors: boolean;
        numberOfErrors: number;
    },
    {
        seasonTicketOwnerCrmId: string;
        fileReference: string;
        pollBulkImportProgress?: () => void;
    },
    ThunkApiConfig
>(
    entryRightTypes.GET_BULK_IMPORT_PROGRESS,
    async ({ seasonTicketOwnerCrmId, fileReference, pollBulkImportProgress }, { rejectWithValue, dispatch }) => {
        try {
            const response = await getBulkImportProgress(seasonTicketOwnerCrmId, fileReference);

            // Call the polling callback if progress is not 100% and callback is provided
            if (pollBulkImportProgress && response.data.progress !== 100) {
                pollBulkImportProgress();
            } else {
                 dispatch(toastActions.success({
                content: {
                    key: "entryRights.startBulkInvitations.success",
                },
            }));
            }

            return {
                progress: response.data.progress,
                hasErrors: response.data.hasErrors,
                numberOfErrors: response.data.numberOfErrors
            };
        } catch (error) {
            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            }
            throw error;
        }
    }
);

// Helper function to extract error code (similar to parking products)
const getFirstErrorCode = (error: AxiosError<ErrorDetailsModel>) => {
    let errorCode = error.message;
    const errors = error.response?.data.errors;
    const fields = error.response?.data.fields;

    if (errors !== undefined && errors.length > 0 &&
        fields !== undefined && fields.length > 0) {
        errorCode = Object.values(errors)[0][fields[0]];
    }

    return errorCode;
};

export const getEntryRightDelegeesMyFleetRequestAction =
    createAction<WithFiltersAndPaging<ForFleetManager>>(request(entryRightTypes.GET_ENTRY_DELEGEES_MYFLEET));
export const getEntryRightDelegeesMyFleetSuccessAction =
    createAction<EntryRightDelegeesWithPagination>(success(entryRightTypes.GET_ENTRY_DELEGEES_MYFLEET));
export const getEntryRightDelegeesMyFleetFailureAction =
    createAction<AxiosError>(failure(entryRightTypes.GET_ENTRY_DELEGEES_MYFLEET));

export const getEntryRightDelegeesMyFleetOverviewThunk = createAsyncThunk<
    EntryRightsMyFleetOverviewWithPagination,
    { request: WithFiltersAndPaging<ForFleetManager>; productContractTypeFilter: ProductContractTypeFilter },
    ThunkApiConfig
>(
    entryRightTypes.GET_ENTRY_DELEGEES_MYFLEET_OVERVIEW,
    async ({ request, productContractTypeFilter }, { rejectWithValue }) => {
        try {
            const response = await getDelegeesFlatMyFleet(request, productContractTypeFilter);
            return response.data;
        } catch (error) {
            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            }

            throw error;
        }
    }
);

export const clearDelegeeDetailsAction = createAction(entryRightTypes.CLEAR_DELEGEE_DETAILS);

export const disposeErrorAction = createAction(entryRightTypes.DISPOSE_ERROR);

export const resendInviteActionThunk = createAsyncThunk(
    entryRightTypes.RESEND_INVITE,
    async (req: ResendInviteModel, { dispatch }) => {
        try {
            await resendInvite(req);

            dispatch(toastActions.success({
                content: {
                    key: "parkingRights.resendInvite.success",
                },
            }));
        }
        catch (error) {
            if( error instanceof AxiosError &&
                error.response?.status == 403){
                dispatch(toastActions.error({
                    content: {
                    key: "parkingRights.resendInvite.rateLimitError",
                    },
                }));
            } else if (error instanceof AxiosError && checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
                dispatch(toastActions.error({
                    content: {
                        key: "errors.invalidSeasonTicketCrmId",
                    },
                }));
            } else{
                dispatch(toastActions.error({
                    content: {
                        key: "parkingRights.resendInvite.error",
                    },
                }));
                throw error;
            }
        }
    }
);

export const resendAllInvitesActionThunk = createAsyncThunk(
    entryRightTypes.RESEND_ALL_INVITES,
    async (req: ResendAllInvitesModel, { dispatch }) => {
        try {
            await resendAllInvites(req);

            dispatch(toastActions.success({
                content: {
                    key: "parkingRights.resendInvites.success",
                },
            }));
        }
        catch (error) {
            if( error instanceof AxiosError &&
                error.response?.status == 403){
                dispatch(toastActions.error({
                    content: {
                    key: "parkingRights.resendInvites.rateLimitError",
                    },
                }));
            } else if (error instanceof AxiosError && checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
                dispatch(toastActions.error({
                    content: {
                        key: "errors.invalidSeasonTicketCrmId",
                    },
                }));
            } else{
                dispatch(toastActions.error({
                    content: {
                        key: "parkingRights.resendInvites.error",
                    },
                }));
                throw error;
            }
        }
    }
);


export const getEntryRightDelegeeDetailsActionThunk = createAsyncThunk<EntryRightDelegeeDetails, { id: string, language: string }>(
    entryRightTypes.GET_ENTRY_RIGHT_DELEGEE,
    async (request, { dispatch }) => {
        try {
            const res = await getDelegeeDetails(request.id, request.language);
            return res.data;
        }
        catch (error) {
            dispatch(toastActions.error({
                content: {
                    key: "pedestrianRights.getDelegeeDetails.error",
                },
            }));
            throw error;
        }
    },
);

export type UpdateEntryRightDelegeeActionThunkPayload = {
    response: UpdateEntryRightResponse;
    original?: EntryRightDelegeeDetails;
};

export const updateEntryRightDelegeeActionThunk = createAsyncThunk<
    ValidationResult<UpdateEntryRightDelegeeActionThunkPayload>,
    UpdateEntryDelegeePayload,
    ThunkApiConfig>(
        entryRightTypes.UPDATE_DELEGEE,
        async (payload: UpdateEntryDelegeePayload, { dispatch, getState }) => {
            const original = getState().entryRights.delegeeDetails.data || undefined;
            const model = {
                ...payload.model,
                pmcs: setTodayActivationDatesToNowIfToday(payload.model.pmcs),
            };

            const showErrorToast = () =>
                dispatch(toastActions.error({
                    content: {
                        key: "parkingRights.updateDelegation.error",
                    },
                }));

            const res = await tryAxios(updateDelegee(model), _ => {
               showErrorToast();
            });

            if (res.ok) {
                dispatch(toastActions.success({
                    content: {
                        key: "parkingRights.updateDelegation.success",
                    },
                }));
                return {
                    ...res,
                    value: {
                        response: res.value,
                        original,
                    }
                };
            }

            if (checkForSeasonTicketCrmIdNotValidError(res.error)) {
                dispatch(toastActions.error({
                    content: {
                        key: "errors.invalidSeasonTicketCrmId",
                    },
                }));
            } else {
                showErrorToast();
            }

            return res;
        }
    );


export const getBulkImportStatusActions = createAsyncThunk<
    GetBulkImportStatusResponseModel,
    string,
    ThunkApiConfig
>(
    entryRightTypes.GET_BULK_IMPORT_STATUS,
    async (seasonTicketOwnerCrmId: string, { rejectWithValue }) => {
        try {
            const response = await getBulkImportStatus(seasonTicketOwnerCrmId);
            return response.data;
        } catch (error) {
            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            }
            throw error;
        }
    }
);

export const resetBulkImportProgressAction = createAction(entryRightTypes.RESET_BULK_IMPORT_PROGRESS);
